import { useEffect, useState } from 'react'
import { interviewerApi } from '../lib/api/interviewer'

/**
 * The timezone an interviewer's session times should be displayed in.
 *
 * Every interviewer-facing page that shows a session time must agree on this.
 * Before this hook existed only the dashboard read the profile timezone, while
 * the evaluation pages fell back to `toLocaleTimeString()` — i.e. the viewer's
 * browser zone. The same 13:30 UTC booking therefore read "9:30 AM" on the
 * dashboard (America/New_York, from the profile) and "7:00 PM" on the evaluation
 * tab (Asia/Kolkata, from the browser).
 *
 * Returns `undefined` until the profile resolves, or if the interviewer has no
 * timezone set. The `formatDateTimeInTz` / `formatDateInTz` helpers accept
 * `undefined` and fall back to browser-local, so callers need no null handling —
 * they just render browser-local for one frame instead of blanking.
 *
 * Mirrors the fetch the dashboard already does (`interviewerApi.getProfile()`,
 * failure tolerated) so the two can never disagree.
 */
export function useInterviewerTimezone(): string | undefined {
  const [timezone, setTimezone] = useState<string | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    interviewerApi
      .getProfile()
      .then((res: unknown) => {
        const profile = (res as { data?: { timezone?: string } })?.data ?? res
        const tz = (profile as { timezone?: string } | null)?.timezone
        if (!cancelled && tz) setTimezone(tz)
      })
      .catch(() => {
        // Non-fatal: the formatters degrade to browser-local.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return timezone
}
